import debounce from 'lodash/debounce';
import {
  Component,
  Host,
  h,
  Event,
  EventEmitter,
  Prop,
  State,
} from '@stencil/core';
import {
  ThumbUpIconOutline,
  ThumbUpIconSolid,
  ThumbDownIconOutline,
  ThumbDownIconSolid,
} from './functional-components';
import { FaqInternalApi } from '../../services/internal-api';
import { VoteType } from '../../services/internal-api/types';

@Component({
  tag: 'webcomet-faq-answer-vote',
  styleUrl: 'faq-answer-vote.scss',
  shadow: true,
})
export class FaqAnswerVote {
  private internalApi: FaqInternalApi.default;

  @State() voteType: VoteType;

  @State() wasPersisted: boolean = false;

  /**
   * The id of the answer
   */
  @Prop() answerId: number;

  /**
   * Controls whether to record votes or not.
   * @internal
   */
  @Prop() disableVotes = false;

  /**
   * The identifier of the FAQ
   */
  @Prop() faqIdentifier: string;

  /**
   * The FAQ language
   */
  @Prop() language: string;

  /**
   * The tag name of the parent component.
   */
  @Prop() parentComponent: string;

  /**
   * Fingerprint of the user's browser
   */
  @Prop() userFingerprint: string;

  /**
   * Whether to use rounded corners on the buttons.
   */
  @Prop() useRoundedCorners: boolean;

  /**
   * Controls the "Did this answer help you?" shown if
   * used feedback is enabled
   */
  @Prop() txtUserFeedback: string;

  /**
   * Controls the "Thank you for your feedback!" text shown
   * after leaving feedback
   */
  @Prop() txtUserFeedbackSaved: string;

  /**
   * Event for when a vote has been persisted
   */
  @Event() votePersisted: EventEmitter<any>;

  componentWillLoad(): void {
    this.submitVote = this.submitVote.bind(this);
    const PERSIST_DEBOUNCE_MS = 500;
    this.persistVote = debounce(this.persistVote, PERSIST_DEBOUNCE_MS);

    this.internalApi = new FaqInternalApi.default();

    if (this.disableVotes) {
      this.internalApi.disableVotes();
    }
  }

  private submitVote(type) {
    if (this.voteType === type) {
      this.voteType = null;

      return;
    } else {
      this.voteType = type;
    }

    this.persistVote();
  }

  private async persistVote() {
    if (this.voteType === null) {
      return;
    }

    const response = await this.internalApi.submitVote(
      this.faqIdentifier,
      this.userFingerprint,
      this.voteType,
      this.answerId,
      this.language
    );

    if ((response && response.ok) || (!response && this.disableVotes)) {
      this.wasPersisted = true;
      this.votePersisted.emit({
        answerId: this.answerId,
        language: this.language,
        type: this.voteType,
      });
    }
  }

  render() {
    return (
      <Host>
        <div class="answer__feedback">
          {!this.wasPersisted ? (
            <div>
              <p class="feedback__text">{this.txtUserFeedback}</p>
              <button
                class={{
                  /* eslint-disable @typescript-eslint/naming-convention */
                  feedback__button: true,
                  'feedback__button--upvote': true,
                  'rounded-corners': this.useRoundedCorners,
                }}
                onClick={() => this.submitVote(VoteType.Upvote)}
              >
                {this.voteType === VoteType.Upvote ? (
                  <ThumbUpIconSolid />
                ) : (
                  <ThumbUpIconOutline />
                )}
              </button>
              <button
                class={{
                  feedback__button: true,
                  'feedback__button--downvote': true,
                  'rounded-corners': this.useRoundedCorners,
                  /* eslint-enable @typescript-eslint/naming-convention */
                }}
                onClick={() => this.submitVote(VoteType.Downvote)}
              >
                {this.voteType === VoteType.Downvote ? (
                  <ThumbDownIconSolid />
                ) : (
                  <ThumbDownIconOutline />
                )}
              </button>
            </div>
          ) : this.wasPersisted ? (
            <p class="feedback__confirmation">{this.txtUserFeedbackSaved}</p>
          ) : (
            ''
          )}
        </div>
      </Host>
    );
  }
}
